import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, EventEmitter, Output } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GlobalsService } from '../../services/globals.service';
import { InventoryService } from '../../services/inventory.service';
import { PrintService } from '../../services/print.service'
import { ReportsService } from '../../services/reports.service'

import * as FileSaver from 'file-saver';
import { animate, query, stagger, state, style, transition, trigger } from '@angular/animations';

@Component({
	selector: 'app-top-hundred',
	templateUrl: './top-hundred.component.html',
	styleUrls: ['./top-hundred.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({
				transform: 'rotate3d(0, 1, 0, 179deg)',
			})),
			state('inactive', style({
				transform: 'rotateY(0)'
			})),
			state('orderlookup', style({
				transform: 'rotateY(179deg)'
			})),
			transition('active => inactive', animate('400ms ease-out')),
			transition('inactive => active', animate('400ms ease-in')),
			transition('inactive => orderlookup', animate('500ms ease-out')),
			transition('orderlookup => inactive', animate('500ms ease-in')),
		]),
		trigger('openClose', [
			state(':enter', style({ height: '*' })),
			state(':leave', style({ height: '0px' })),
			transition('false <=> true', animate(500))
		]),
		trigger('filterAnimation', [
			transition(':enter, * => 0, * => -1', []),
			transition(':increment', [
				query(':enter', [
					style({ opacity: 0, width: 0 }),
					stagger(50, [
						animate('300ms ease-out', style({ opacity: 1, width: '*' })),
					]),
				], { optional: true })
			]),
			transition(':decrement', [
				query(':leave', [
					stagger(50, [
						animate('300ms ease-out', style({ opacity: 0, width: 0 })),
					]),
				])
			]),
		]),
		trigger('grow', [ // Note the trigger name
			transition(':enter', [
				// :enter is alias to 'void => *'
				style({ height: '0', width: '0' }),
				animate(200, style({ height: '*', width: '*' })),
				animate('200ms', style({ opacity: 1 })),
			]),
			transition(':leave', [
				// :leave is alias to '* => void'
				animate(100, style({ height: 0, width: 0 })),
				animate('100ms', style({ opacity: 0 }))
			])
		]),
	]
})
export class TopHundredComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();
	private subs: Subscription[] = [];
	@Input() customer = null;
	@Input() vendor = null;
	@Input() menuapp = true;
	@Input() dashboard = false;
	/** When set with [dashboard]="true", default row limit (otherwise dashboard uses 25). */
	@Input() dashboardItemLimit?: number;
	@ViewChild('print_table') printtable: ElementRef;
	searchForm = null;
	categories: any = [];
	productlines: any = [];
	filename: any = 'Top_100_Sales_';
	brands: any = [];
	user: any = [];
	results: any = [];
	salespeople: any = [];
	sending: any = false;
	loading = false;
	reportResults: any = [];
	sortedReportResults: any = [];
	sortByColumn: string;
	sortDirection: string;
	color: any = 'blue';

	@Input() flip = 'inactive';
	@Output() viewData = new EventEmitter < any > ();
	viewing: any = false;
	data: any = false;
	title = 'Top 100';
	top100: any = 'notready';
	locations: any[] = [];

	constructor(private reportService: ReportsService, private printService: PrintService, private fb: UntypedFormBuilder, private inventoryService: InventoryService, private globalSearchService: GlobalSearchService) {}

	private getDashboardDefaultLimit(): number {
		const n = this.dashboardItemLimit;
		return typeof n === 'number' && !isNaN(n) && n >= 1 ? Math.min(500, Math.floor(n)) : 25;
	}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		if (this.dashboard) {
			const lim = this.getDashboardDefaultLimit();
			this.title = 'Top ' + lim + ' Items';
		}

		this.subs.push(
			this.globalSearchService.user.subscribe(result => {
				this.user = result;
				const now = new Date();
				const dateTo = new Date(now);
				const dateFrom = new Date(now);
				dateFrom.setDate(dateFrom.getDate() - 30);
				const defaultLoc = result?.user?.defaultlocation?.loccode || '';
				const defaultLocArr = defaultLoc ? [defaultLoc] : [];
				if (this.searchForm) {
					this.searchForm.patchValue({
						'user': this.user.user.userid,
						'loccode': defaultLocArr
					}, { emitEvent: false });
					const inList = this.locations.length && this.locations.some((l: any) => l.loccode === defaultLoc);
					if (inList) {
						this.searchForm.get('loccode').setValue(defaultLocArr, { emitEvent: false });
					}
				} else {
					this.searchForm = this.fb.group({
						'customer': this.customer,
						'vendor': this.vendor,
						'brand': '',
						'prodline': '',
						'salesperson': '',
						'category': '',
						'limit': this.dashboard ? this.getDashboardDefaultLimit() : 100,
						'user': this.user.user.userid,
						'datefrom': dateFrom,
						'dateto': dateTo,
						'loccode': defaultLocArr
					});
					this.tophundred();
				}
			})
		);

		this.subs.push(
			this.globalSearchService.locations.subscribe((locs: any) => {
				this.locations = locs || [];
				if (this.searchForm && this.locations.length) {
					const defaultCode = this.user?.user?.defaultlocation?.loccode || '';
					const inList = this.locations.some((l: any) => l.loccode === defaultCode);
					const current = this.searchForm.get('loccode').value;
					const currentArr = Array.isArray(current) ? current : (current ? [current] : []);
					const currentInList = currentArr.length && currentArr.every((c: string) => this.locations.some((l: any) => l.loccode === c));
					if (inList) {
						this.searchForm.get('loccode').setValue([defaultCode], { emitEvent: false });
					} else if (!currentInList && currentArr.length === 0) {
						this.searchForm.get('loccode').setValue([this.locations[0].loccode], { emitEvent: false });
					}
				}
			})
		);

		this.subs.push(this.inventoryService.getItemCategories().subscribe(c => { this.categories = c; }));
		this.subs.push(this.inventoryService.getItemProductlines().subscribe(p => { this.productlines = p; }));
		this.subs.push(this.inventoryService.getBrands().subscribe((results: any) => { this.brands = results; }));
		this.subs.push(this.reportService.getSalesPeople().subscribe((results: any) => { this.salespeople = results; }));
	}

	ngOnDestroy(): void {
		this.subs.forEach(s => { if (s) s.unsubscribe(); });
		this.subs.length = 0;
		this.destroy$.next();
		this.destroy$.complete();
	}

	tophundred() {
		if (!this.searchForm) return;
		this.sending = true;
		this.loading = true;
		this.inventoryService.top100ItemsReport(this.searchForm.value).pipe(takeUntil(this.destroy$)).subscribe((p: any) => {
			this.loading = false;
			this.sending = false;
			this.reportResults = p;
			this.sortedReportResults = p;
			this.top100 = 'ready';
			this.data = p;
		});
	}

	exportPdf() {
		const now = new Date();
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const data = {
			content: encoded,
			filename: this.getExportFilename(),
			title: `Top ${this.searchForm?.get('limit')?.value || 100} Item Sales - Created @ ${now.toLocaleString()}`,
			subtitle: this.getReportSubtitle()
		};

		this.printService.pdf(data).pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
		});
	}

	exportXls() {
		const now = new Date();
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const data = {
			content: encoded,
			filename: this.getExportFilename(),
			title: `Top ${this.searchForm?.get('limit')?.value || 100} Item Sales - Created @ ${now.toLocaleString()}`,
			subtitle: this.getReportSubtitle()
		};

		this.printService.xls(data).pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
		});
	}

	getExportFilename(): string {
		const now = new Date();
		const suffix = (now.getMonth() + 1) + '' + now.getDate() + now.getFullYear();
		if (this.dashboard && this.searchForm) {
			const from = this.searchForm.get('datefrom').value;
			const to = this.searchForm.get('dateto').value;
			const fromStr = from ? new Date(from).toISOString().slice(0, 10) : '';
			const toStr = to ? new Date(to).toISOString().slice(0, 10) : '';
			return this.filename + (fromStr && toStr ? fromStr + '_' + toStr + '_' : '') + suffix;
		}
		return this.filename + suffix;
	}

	getReportSubtitle() {
		let out = '';

		if (this.customer) {
			out += ' for Customer ' + this.customer.customer.brname;
		} else if (this.vendor) {
			out += ' for Vendor ' + this.vendor.name;
		}
		if (this.dashboard && this.searchForm) {
			const from = this.searchForm.get('datefrom').value;
			const to = this.searchForm.get('dateto').value;
			if (from && to) {
				out += (out ? ' | ' : '') + new Date(from).toLocaleDateString() + ' – ' + new Date(to).toLocaleDateString();
			}
		}
		return out;
	}

	sortBy(property: string): void {
		if (this.sortByColumn === property) {
			// Toggle sort direction if the same column is clicked
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			// Default to ascending order when a new column is clicked
			this.sortDirection = 'asc';
			this.sortByColumn = property;
		}

		this.sortedReportResults.sort((a, b) => {
			// Check if the property values are numeric
			const isNumeric = !isNaN(parseFloat(a[property])) && isFinite(a[property]) && !isNaN(parseFloat(b[property])) && isFinite(b[property]);

			if (isNumeric) {
				// Sort as numbers if the property values are numeric
				const numA = parseFloat(a[property]);
				const numB = parseFloat(b[property]);
				return this.sortDirection === 'asc' ? numA - numB : numB - numA;
			} else {
				// Sort as strings if the property values are not numeric
				const comparison = a[property].localeCompare(b[property]);
				return this.sortDirection === 'asc' ? comparison : -comparison;
			}
		});
	}


	sortAscending(data: any[], property: string): any[] {
		return data.sort((a, b) => {
			if (a[property] < b[property]) { return -1; }
			if (a[property] > b[property]) { return 1; }
			return 0;
		});
	}

	sortDescending(data: any[], property: string): any[] {
		return data.sort((a, b) => {
			if (a[property] > b[property]) { return -1; }
			if (a[property] < b[property]) { return 1; }
			return 0;
		});
	}

	toggleFlip(data: any, title: string) {
		if (!data) {
			this.flip = 'inactive';
		} else {
			this.flip = (this.flip == 'active') ? 'inactive' : 'active';
		}

		if (this.flip == 'active') {
			this.viewing = data;
			this.title = this.dashboard && this.searchForm
				? 'Top ' + (this.searchForm.get('limit').value || 25) + ' Items'
				: title;
			const dashboard_data = { data: data, title: this.title };
			this.viewData.emit(dashboard_data);
		} else {
			this.viewData.emit(false);
		}
	}

	onDashboardLimitChange() {
		if (!this.dashboard || !this.searchForm) return;
		const ctrl = this.searchForm.get('limit');
		let n = parseInt(ctrl.value, 10);
		if (isNaN(n) || n < 1) n = 25;
		if (n > 500) n = 500;
		ctrl.setValue(n, { emitEvent: false });
		this.title = 'Top ' + n + ' Items';
		this.tophundred();
	}

	onDashboardDateChange() {
		if (!this.dashboard || !this.searchForm) return;
		this.tophundred();
	}

	onDashboardLocationChange() {
		if (!this.dashboard || !this.searchForm) return;
		this.tophundred();
	}

}